import type { AgentClient } from '../api/AgentClient';
import type { ExtractorRegistry } from '../server/documents/extractors';
import type {
  SiteReadHandler,
  SiteReadRegistry,
} from '../server/SiteReadHandler';

/**
 * Everything a site is allowed to change about the assistant.
 *
 * This is the add-on's public seam. A customer add-on lists
 * `@interaktiv/volto-ai-editor` in its own `addons` and, from its
 * `applyConfig`, reaches the registry through `aiEditor(config)`:
 *
 * ```ts
 * import { aiEditor } from '@interaktiv/volto-ai-editor';
 *
 * export default function applyConfig(config) {
 *   aiEditor(config)
 *     .useClient(new AcmeAgentClient())        // a different backend
 *     .addSiteRead(AcmeProductsHandler)        // one more thing to read
 *     .replaceSiteRead('get_page', AcmeGetPage)
 *     .addExtractor(new DocxExtractor());      // one more file format
 *
 *   return config;
 * }
 * ```
 *
 * Registration is deferred: the site reads are constructed when the express
 * middleware is built, which is after every add-on's `applyConfig` has run,
 * so the order add-ons load in does not decide who wins. What decides is the
 * order of the calls — last one wins, as everywhere else in Volto's config.
 */
export type SiteReadFactory = new (
  ...args: ConstructorParameters<typeof SiteReadHandler>
) => SiteReadHandler<any>;

export class AiEditorRegistry {
  /** Which client the UI talks to. `undefined` means "decide from settings". */
  client?: AgentClient;

  protected readonly siteReadFactories: SiteReadFactory[] = [];
  protected readonly extractorFactories: (() => unknown)[] = [];

  useClient(client: AgentClient): this {
    this.client = client;

    return this;
  }

  /** Add a read the stock agent does not know about, or replace one it does. */
  addSiteRead(factory: SiteReadFactory): this {
    this.siteReadFactories.push(factory);

    return this;
  }

  /** Sugar for `addSiteRead`, when the intent is to shadow a stock read. */
  replaceSiteRead(name: string, factory: SiteReadFactory): this {
    if (!name) throw new Error('replaceSiteRead needs the name it replaces.');

    return this.addSiteRead(factory);
  }

  addExtractor(factory: () => unknown): this {
    this.extractorFactories.push(factory);

    return this;
  }

  /** Apply everything registered to the stock registries. Server-side only. */
  applyTo(reads: SiteReadRegistry, deps: any, extractors?: ExtractorRegistry) {
    this.siteReadFactories.forEach((Factory) =>
      reads.register(new Factory(deps)),
    );
    if (extractors) {
      this.extractorFactories.forEach((factory) =>
        extractors.register(factory() as never),
      );
    }

    return reads;
  }
}

interface WithAiEditor {
  settings: Record<string, any>;
}

/** The one registry of a Volto config, created on first use. */
export function aiEditor(config: WithAiEditor): AiEditorRegistry {
  const settings = config.settings as Record<string, any>;
  if (!settings.aiEditor) settings.aiEditor = {};
  if (!settings.aiEditor.registry) {
    settings.aiEditor.registry = new AiEditorRegistry();
  }

  return settings.aiEditor.registry as AiEditorRegistry;
}
